import React, { createContext } from 'react';
import { useTranslation } from 'react-i18next';
import DraftConflictModal from '@components/atoms/modals/draft-conflict-modal.tsx';
import { openCloudVersionInNewTab } from '@utils/localStorage/draft-conflict.ts';
import type {
  TranscriptContextType,
  TranscriptProviderProps
} from './transcript-context-state';
import { useTranscriptContextLogic } from './use-transcript-context-logic';

export type { TranscriptContextType };

export const TranscriptContext = createContext<
  TranscriptContextType | undefined
>(undefined);

export function TranscriptProvider({
  children,
  session,
  document
}: TranscriptProviderProps) {
  const { t } = useTranslation();
  const {
    contextValue,
    conflict,
    resolveConflictKeepLocal,
    resolveConflictLoadCloud
  } = useTranscriptContextLogic({ session, document });

  return (
    <TranscriptContext.Provider value={contextValue}>
      {conflict ? (
        <DraftConflictModal
          isOpen
          localSavedAt={conflict.localTimestamp}
          serverSavedAt={conflict.serverTimestamp}
          localCount={conflict.localCount}
          serverCount={conflict.serverCount}
          unit={t('transcript-segments-unit', 'segments')}
          onKeepLocal={resolveConflictKeepLocal}
          onLoadCloud={resolveConflictLoadCloud}
          onViewCloud={openCloudVersionInNewTab}
        />
      ) : (
        children
      )}
    </TranscriptContext.Provider>
  );
}

export function useTranscript() {
  const context = React.useContext(TranscriptContext);
  if (context === undefined) {
    throw new Error('useTranscript must be used within a TranscriptProvider');
  }
  return context;
}
